import type { ReactNode } from 'react';
import type { WorkOrderDto } from '@signalvote/shared';
import {
  ImportJobStatus,
  VOTER_DATA_FILE_RETENTION_DAYS,
  WorkOrderStatus,
  WorkOrderType,
  WORK_ORDER_TYPE_LABELS,
  assertNever,
  isVoterDataImporting,
  textSendRecipientsInWords,
  workOrderCustomerStatusLabel,
} from '@signalvote/shared';
import { Button, Card, CardContent, CardHeader, CardTitle, buttonVariants } from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { formatInCampaignTimeZone } from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';
import { voterDataCountPromise, voterDataRecordsLabel } from './voterDataOrder';
import { FulfilmentRecipeView, VoterDataFulfilForm } from './VoterDataFulfilment';
import type { FulfilVoterDataInput } from './VoterDataFulfilment';
import { SendChecklist, type SendProgress } from './SendChecklist';
import { describeSendTime, timeZoneName } from './workOrderSchedule';
import { useCampaignStore } from '@/stores';

interface WorkOrderDetailViewProps {
  workOrder: WorkOrderDto;
  /**
   * Pays a Draft: re-quotes it, confirms the exact price, then checks it out for the card form. Only a
   * Voter data Draft passes this — an order placed from the builder and not paid.
   */
  onPay?: () => void;
  /** Why an unpaid order (a Draft, or an Invoiced one) can't be paid now, said where Pay or its card form would be. */
  payUnavailable?: string;
  /**
   * Fulfils a Paid Voter data order: attaches the CSV exported from L2 with its delivered count.
   * Only a platform admin's view passes this.
   */
  onFulfil?: (input: FulfilVoterDataInput) => void;
  fulfilling?: boolean;
  fulfilError?: string | null;
  /** Downloads a Complete Voter data order's file. Offered until the file expires. */
  onDownload?: () => void;
  downloading?: boolean;
  downloadError?: string | null;
  /**
   * Imports a Complete Voter data order's file into the campaign's voters. Offered until the file
   * expires.
   */
  onImport?: () => void;
  importing?: boolean;
  importError?: string | null;
  /** The import has started; its progress shows in the import banner at the top of the page. */
  importStarted?: boolean;
  /**
   * The campaign's subscription has lapsed. Importing is part of the subscription, so it's replaced
   * by a prompt to subscribe; the download still works.
   */
  importLocked?: boolean;
  /** A paid Text send's progress, shown as the same checklist its status link shows. */
  progress?: SendProgress;
  /** Paying an Invoiced order by card in the page. */
  payment?: ReactNode;
  /** A Complete Text send's results report: its download, and a platform admin's upload. */
  results?: ReactNode;
}

/**
 * The read-only detail for a Work Order that is no longer editable — a customer opens this from
 * the jobs list to see exactly what a job contains and its friendly status once it has left
 * Draft. The body renders by type: a text send shows its message, image, reach, schedule and
 * amount; Voter data shows its sentence, what's billed, the quoted counts, the columns delivered
 * and the price — and, for a platform admin, the fulfilment recipe. It is presentational: no edit
 * affordance. It hosts the card payment it is handed while the job is Invoiced, for a platform admin
 * on a Paid Voter data order, the form that delivers its file, and on a delivered Voter data order,
 * its download and import into the campaign's voters (or "file expired" once the file is past its
 * retention).
 */
export function WorkOrderDetailView({
  workOrder,
  onPay,
  payUnavailable,
  onFulfil,
  fulfilling = false,
  fulfilError,
  onDownload,
  downloading = false,
  downloadError,
  onImport,
  importing = false,
  importError,
  importStarted = false,
  importLocked = false,
  progress,
  payment,
  results,
}: WorkOrderDetailViewProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle>{WORK_ORDER_TYPE_LABELS[workOrder.type]}</CardTitle>
          <p className="text-xs text-muted-foreground">Order {workOrder.reference}</p>
        </div>
        <Badge data-testid="detail-status" variant={statusBadgeVariant(workOrder.status)}>
          {workOrderCustomerStatusLabel(workOrder.status, workOrder.type)}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {progress && <SendChecklist progress={progress} signedIn />}
        {results}
        <DetailBody workOrder={workOrder} />

        {payment}

        {workOrder.voterData && workOrder.status === WorkOrderStatus.Complete && workOrder.voterData.fileExpiresAt && (
          <VoterDataFile
            fileExpiresAt={workOrder.voterData.fileExpiresAt}
            fileExpired={workOrder.voterData.fileExpired}
            onDownload={onDownload}
            downloading={downloading}
            error={downloadError}
          />
        )}

        {onImport && workOrder.voterData && workOrder.status === WorkOrderStatus.Complete
          && workOrder.voterData.fileExpiresAt && !workOrder.voterData.fileExpired && (
          <VoterDataImport
            onImport={onImport}
            importing={importing}
            importStatus={workOrder.voterData.importStatus}
            started={importStarted}
            locked={importLocked}
            error={importError}
          />
        )}

        {onFulfil && workOrder.voterData && workOrder.status === WorkOrderStatus.Paid && (
          <VoterDataFulfilForm
            quotedRecords={workOrder.voterData.records}
            exportProgress={workOrder.voterData}
            onFulfil={onFulfil}
            fulfilling={fulfilling}
            error={fulfilError}
          />
        )}

        {onPay && workOrder.status === WorkOrderStatus.Draft && (
          <Button className="w-full" onClick={onPay}>
            {`Pay ${workOrder.amount === null ? '' : formatAmount(workOrder.amount)}`.trim()}
          </Button>
        )}

        {payUnavailable && (workOrder.status === WorkOrderStatus.Draft || workOrder.status === WorkOrderStatus.Invoiced) && (
          <p className="text-sm text-muted-foreground">{payUnavailable}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A delivered Voter data file: its download and how long it's kept, or — once the file is past its
 * retention and deleted — a plain "file expired" instead of a link that would fail.
 */
function VoterDataFile({
  fileExpiresAt,
  fileExpired,
  onDownload,
  downloading,
  error,
}: {
  fileExpiresAt: string;
  fileExpired: boolean;
  onDownload?: () => void;
  downloading: boolean;
  error?: string | null;
}) {
  const expiresOn = formatInCampaignTimeZone(fileExpiresAt);

  if (fileExpired) {
    return (
      <div data-testid="voter-data-file-expired" className="space-y-1 rounded-md border p-3 text-sm">
        <p className="font-medium">File expired</p>
        <p className="text-muted-foreground">
          Files are kept for {VOTER_DATA_FILE_RETENTION_DAYS} days after delivery. This one expired {expiresOn}.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {onDownload && (
        <Button className="w-full" onClick={onDownload} disabled={downloading}>
          {downloading ? 'Preparing download…' : 'Download file'}
        </Button>
      )}
      <p data-testid="voter-data-file-available" className="text-xs text-muted-foreground">
        Available until {expiresOn}. Files are kept for {VOTER_DATA_FILE_RETENTION_DAYS} days after delivery.
      </p>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Importing a delivered Voter data file into the campaign's voters. Delivery starts the import, so
 * this usually shows where it stands; Import is offered only when none is running or done (it failed,
 * or never started). Importing is part of the subscription, so a lapsed campaign sees a prompt to
 * subscribe in place of Import.
 */
function VoterDataImport({
  onImport,
  importing,
  importStatus,
  started,
  locked,
  error,
}: {
  onImport: () => void;
  importing: boolean;
  importStatus: ImportJobStatus | null;
  started: boolean;
  locked: boolean;
  error?: string | null;
}) {
  if (importStatus === ImportJobStatus.Completed) {
    return (
      <p data-testid="voter-data-imported" className="rounded-md border p-3 text-sm">
        Imported into your voters.
      </p>
    );
  }

  if (started || isVoterDataImporting(importStatus)) {
    return (
      <p data-testid="voter-data-import-started" className="rounded-md border p-3 text-sm">
        Importing into your voters. Progress shows at the top of the page.
      </p>
    );
  }

  if (locked) {
    // A plain link: the billing page is paywall-exempt, so a lapsed campaign can reach it to pay.
    return (
      <a href="/app/settings/billing" className={cn(buttonVariants({ variant: 'outline' }), 'w-full')}>
        Subscribe to import these into your voters
      </a>
    );
  }

  return (
    <div className="space-y-2">
      <Button variant="outline" className="w-full" onClick={onImport} disabled={importing}>
        {importing ? 'Starting import…' : 'Import into my voters'}
      </Button>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function DetailBody({ workOrder }: { workOrder: WorkOrderDto }) {
  switch (workOrder.type) {
    case WorkOrderType.TextSend:
      return <TextSendDetailBody workOrder={workOrder} />;
    case WorkOrderType.VoterData:
      return workOrder.voterData ? (
        <VoterDataDetailBody workOrder={workOrder} />
      ) : (
        <AmountOnly amount={workOrder.amount} />
      );
    default:
      return assertNever(workOrder.type);
  }
}

function AmountOnly({ amount }: { amount: number | null }) {
  if (amount === null) return null;
  return (
    <div className="space-y-1 text-sm">
      <p className="text-xs font-medium text-muted-foreground">Amount</p>
      <p className="tabular-nums">{formatAmount(amount)}</p>
    </div>
  );
}

function VoterDataDetailBody({ workOrder }: { workOrder: WorkOrderDto }) {
  const voterData = workOrder.voterData!;
  return (
    <>
      <p data-testid="voter-data-sentence" className="text-base font-medium leading-snug">
        {voterData.sentence || 'Voter list'}
      </p>

      {/* The platform admin's rebuild instructions sit right under what the customer meant. */}
      {voterData.recipe && <FulfilmentRecipeView recipe={voterData.recipe} />}

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Records</p>
          <p data-testid="voter-data-records" className="tabular-nums">
            {voterDataRecordsLabel(voterData.records, voterData.onePerHousehold)}
          </p>
          <p data-testid="voter-data-quoted" className="text-xs text-muted-foreground tabular-nums">
            {voterData.quotedCount.toLocaleString()} voters in{' '}
            {voterData.quotedHouseholds.toLocaleString()} households
          </p>
        </div>
        {workOrder.amount !== null && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Price</p>
            <p className="tabular-nums">{formatAmount(workOrder.amount)}</p>
          </div>
        )}
      </div>

      {voterData.deliveredCount === null && (
        <p data-testid="voter-data-count-promise" className="text-xs text-muted-foreground">
          {voterDataCountPromise(voterData.records, voterData.onePerHousehold)}
        </p>
      )}

      {voterData.deliveredCount !== null && (
        <div className="space-y-1 text-sm">
          <p className="text-xs font-medium text-muted-foreground">Delivered</p>
          <p data-testid="voter-data-delivered" className="tabular-nums">
            {voterData.deliveredCount.toLocaleString()} records
          </p>
        </div>
      )}

      {voterData.columns.length > 0 && (
        <div className="space-y-1 text-sm">
          <p className="text-xs font-medium text-muted-foreground">Columns you'll receive</p>
          <p data-testid="voter-data-columns" className="text-muted-foreground">
            {voterData.columns.join(', ')}
          </p>
        </div>
      )}

    </>
  );
}

function TextSendDetailBody({ workOrder }: { workOrder: WorkOrderDto }) {
  const { details } = workOrder;
  const previewUrl = details.imagePreviewUrl;

  // The frozen invoice-time snapshot is authoritative once captured; fall back to the live count.
  const recipientCount = details.recipientCount ?? workOrder.recipientCount;
  const timeZoneId = useCampaignStore((state) => state.selectedCampaign?.timeZoneId);
  const schedule = formatSchedule(workOrder, timeZoneId);
  // What voters get, as the status link page shows it; a body alone until the disclaimer is set.
  const message = details.assembledMessage ?? details.messageText;

  return (
    <>
      <p data-testid="recipient-reach" className="text-sm">
        This texts {textSendRecipientsInWords(details.recipientSource, recipientCount, workOrder.status)}.
      </p>

      {previewUrl && (
        <img
          data-testid="image-preview"
          src={previewUrl}
          alt="Attached text-send creative"
          className="max-h-48 max-w-full rounded-md border border-input object-contain"
        />
      )}

      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Message</p>
        <div
          data-testid="detail-message"
          className={cn(
            'min-h-24 whitespace-pre-wrap [overflow-wrap:anywhere] rounded-md border border-input bg-muted/30 p-3 text-sm',
            !message && 'text-muted-foreground',
          )}
        >
          {message || 'No message yet'}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Send</p>
          <p data-testid="detail-send-time">{schedule ?? 'Not scheduled'}</p>
        </div>
        {workOrder.amount !== null && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Amount</p>
            <p className="tabular-nums">{formatAmount(workOrder.amount)}</p>
          </div>
        )}
      </div>
    </>
  );
}

function statusBadgeVariant(status: WorkOrderStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case WorkOrderStatus.Complete:
      return 'default';
    case WorkOrderStatus.Failed:
      return 'destructive';
    case WorkOrderStatus.Cancelled:
      return 'secondary';
    case WorkOrderStatus.Draft:
    case WorkOrderStatus.SentToVendor:
    case WorkOrderStatus.VendorPaid:
    case WorkOrderStatus.Invoiced:
    case WorkOrderStatus.Paid:
    default:
      return 'outline';
  }
}

function formatAmount(amount: number): string {
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

function formatSchedule(workOrder: WorkOrderDto, timeZoneId: string | undefined): string | null {
  const when = describeSendTime(workOrder.sendDate, workOrder.sendTime);
  const zone = timeZoneName(timeZoneId);
  return when && zone ? `${when}, ${zone}` : when;
}
